import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FileText } from "lucide-react";
import { JobWithShare } from "@/lib/supabase/supabaseTypes";
import { jobStatusToLabel, JobStatusEnum } from "@/lib/types/job";
import { Loading } from "@/components/src/animations/loading";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../components/ui/table";

interface VendorJobsTableProps {
	jobs: JobWithShare[];
	vendorOrgId: string;
	loading: boolean;
	patientNames: Record<string, string>;
}

export const VendorJobsTable: React.FC<VendorJobsTableProps> = ({
	jobs,
	vendorOrgId,
	loading,
	patientNames,
}) => {
	const navigate = useNavigate();

	const handleRowClick = useCallback(
		(jobId: number) => {
			navigate(`/vendor/${vendorOrgId}/job/${jobId}`);
		},
		[navigate, vendorOrgId]
	);

	if (loading) {
		return <Loading />;
	}

	if (jobs.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center h-full gap-2">
				<FileText size={32} className="text-stone-300" />
				<span className="text-sm text-stone-400">
					Keine Aufträge vorhanden
				</span>
			</div>
		);
	}

	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>Auftrag</TableHead>
					<TableHead>Titel</TableHead>
					<TableHead>Patient</TableHead>
					<TableHead>Status</TableHead>
					<TableHead>Erstellt</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{jobs.map((job) => (
					<TableRow
						key={job.id}
						className="cursor-pointer"
						onClick={() => handleRowClick(job.id!)}
						aria-label={`Auftrag ${job.code}`}
						data-testid={`vendor-jobs-table-row-${job.id}`}
					>
						<TableCell className="font-medium">
							{job.code}
						</TableCell>
						<TableCell>{job.title}</TableCell>
						<TableCell className="text-stone-500">
							{(job.patient_id && patientNames[job.patient_id]) ||
								"–"}
						</TableCell>
						<TableCell className="text-stone-500">
							{jobStatusToLabel[job.status as JobStatusEnum] ??
								"–"}
						</TableCell>
						<TableCell className="text-stone-500">
							{job.created_at
								? new Date(job.created_at).toLocaleDateString(
										"de-CH",
										{
											day: "2-digit",
											month: "2-digit",
											year: "2-digit",
										}
									)
								: "–"}
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	);
};
